/**
 * The island's theme plumbing — semantic tokens in, CSS custom properties out.
 *
 * Two constraints shape this:
 *
 * 1. **The island must stay WebView-hostable (D18)** — it cannot own a theme,
 *    reach for app state, or import a renderer. So the host injects a
 *    `TokenResolver` (already the prop the grid takes for conditional
 *    formatting) and everything the chrome paints is resolved through that one
 *    function. With no resolver injected we fall back to the contract's default
 *    theme, so a standalone mount still looks right rather than rendering the
 *    literal string `"surface.alt"` as a color.
 * 2. **Chrome is styled with module-level style objects**, which cannot call a
 *    hook. Publishing the resolved tokens as CSS variables on the island root
 *    lets those constants say `var(--qf-surface)` and inherit — so a theme
 *    switch is one inline-style change on one element, and no component needs
 *    to know a theme exists.
 *
 * The same call also emits AG Grid's own `--ag-*` variables, because the grid
 * paints its rows, headers and borders from those. They must land **on the
 * element carrying the `ag-theme-quartz*` class** (inline, so they beat the
 * theme class's own declarations on that same element) — an ancestor's value
 * would simply be overridden.
 */

import { DEFAULT_THEME, UI_CSS_VARS, resolveColorRef } from "@qufolio/model";
import type { CSSProperties } from "react";

import type { TokenResolver } from "./columns.js";

/**
 * Token resolution with no host-supplied theme: the contract's default.
 *
 * Deliberately not a passthrough — an unresolved `"surface.alt"` reaching CSS
 * paints nothing and the panel renders transparent-on-transparent.
 */
export const defaultTokenResolver: TokenResolver = (token) =>
  resolveColorRef(token, DEFAULT_THEME) ?? token;

/**
 * The chrome vocabulary, shared with the app shell (`UI_CSS_VARS` in the
 * contract package) so the island and its host cannot drift onto different
 * variable names. Adding a color means adding a *token*, never a hex (D13).
 */
export const CHROME_VARS = UI_CSS_VARS;

/**
 * Every CSS variable the island needs, resolved: the `--qf-*` chrome set plus
 * AG Grid's `--ag-*` equivalents.
 *
 * The grid's canvas reads `background` — the *same* token the chart canvas
 * paints with — so a black theme is black across both surfaces instead of a
 * black chart beside a grey grid.
 */
export function themeCssVars(resolve: TokenResolver = defaultTokenResolver): CSSProperties {
  const vars: Record<string, string> = {};
  for (const [cssVar, token] of Object.entries(CHROME_VARS)) vars[cssVar] = resolve(token);

  const background = resolve("background");
  const surface = resolve("surface");
  const surfaceAlt = resolve("surface.alt");
  const border = resolve("border");
  const text = resolve("text");
  const textSecondary = resolve("text.secondary");
  const accent = resolve("accent");

  Object.assign(vars, {
    // **The host's font, not Quartz's.** The theme's own `--ag-font-family`
    // leads with `"IBM Plex Sans"`, which nothing installs and nothing loads —
    // so the grid was falling through to the next entry and matching the shell
    // by accident. `inherit` makes that deliberate: the app names one font in
    // one place and the grid follows it, including the tabular figures set on
    // `body` that keep a column of prices aligned.
    "--ag-font-family": "inherit",
    "--ag-background-color": background,
    "--ag-odd-row-background-color": resolve("row.alt"),
    "--ag-row-hover-color": resolve("row.hover"),
    "--ag-selected-row-background-color": resolve("selection"),
    "--ag-foreground-color": text,
    "--ag-data-color": text,
    "--ag-secondary-foreground-color": textSecondary,
    "--ag-disabled-foreground-color": resolve("text.muted"),
    "--ag-header-background-color": surfaceAlt,
    "--ag-header-foreground-color": textSecondary,
    "--ag-subheader-background-color": surfaceAlt,
    "--ag-border-color": border,
    "--ag-secondary-border-color": border,
    "--ag-row-border-color": border,
    // **Column lines** (owner, 2026-10-06): a thin rule between cells, in the
    // row border's colour so the grid reads as one ruled table. Quartz leaves
    // it `transparent`; with 3px cell padding a side, neighbouring numbers
    // otherwise run together.
    "--ag-cell-horizontal-border": `solid ${border}`,
    "--ag-header-column-resize-handle-color": border,
    "--ag-input-border-color": border,
    "--ag-input-focus-border-color": accent,
    "--ag-active-color": accent,
    "--ag-range-selection-background-color": resolve("selection"),
    "--ag-control-panel-background-color": surface,
    "--ag-menu-background-color": surface,
    "--ag-menu-border-color": border,
    "--ag-panel-background-color": surface,
    "--ag-panel-border-color": border,
    "--ag-tooltip-background-color": surface,
    "--ag-chip-background-color": resolve("selection"),
    "--ag-chip-border-color": border,
    "--ag-invalid-color": resolve("negative"),

    /**
     * Density. Everything above is colour; these are the only sizing overrides,
     * and they are here because Quartz's defaults are built for a form, not for
     * a screen of a hundred numeric columns.
     *
     * `--ag-grid-size: 8px` gives `--ag-cell-horizontal-padding` of 16px, so
     * **every column spends 32px on whitespace before it draws anything**. At
     * ten visible columns that is 320px of the panel — on a docked screener,
     * more than a column's worth of real estate for nothing. 6px a side handed
     * ~200px back; 3px a side (owner, 2026-10-05: "very less margins", with
     * many columns on screen) hands back another ~60px at ten columns.
     *
     * `--ag-grid-size` itself is left alone: it also drives row height, header
     * height and every menu's padding, so tightening it here would shrink the
     * chrome along with the cells and make the header row cramped.
     *
     * One variable covers both halves: `.ag-header-cell` and `.ag-row >
     * .ag-cell-wrapper` both read it, so a header cannot drift out of line with
     * the values under it.
     */
    "--ag-cell-horizontal-padding": "3px",

    /**
     * 13px, one step under Quartz's 14. A screener is read by scanning a column
     * of figures rather than by reading a sentence, and the figures are now
     * tabular — same advance width per digit — so a step down costs less
     * legibility here than it would in prose and buys a column or two of width
     * back on a docked panel.
     *
     * It tightens the rows as well without being asked to: Quartz derives
     * `--ag-row-height` as `font-size + grid-size × 3.5`, so this is 41px rather
     * than 42. That is the intended direction — the height is set by
     * `--ag-grid-size`, which stays put for the reason above.
     */
    "--ag-font-size": "13px",
  });

  return vars as CSSProperties;
}

/**
 * The AG Grid theme class for a color mode.
 *
 * The `--ag-*` overrides above do most of the work, but the class still decides
 * the icon set and shadow treatment, and starting from the wrong one leaves
 * light icons on a white grid.
 */
export function agThemeClass(mode: "light" | "dark" | "auto" | undefined): string {
  return mode === "light" ? "ag-theme-quartz" : "ag-theme-quartz-dark";
}
